import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { getCdnUrl } from "../utils/cdnMapping";
import { Generation, QueueStatus, User, panoApi } from "../services/panoApi";

const promptExamples = [
  "A vast canyon landscape at sunrise with amber cliffs and a winding river.",
  "An outdoor music festival crowd in front of a massive twilight stage.",
  "A neon cyberpunk city highway chase with a black hypercar.",
];

const statusTone: Record<string, string> = {
  queued: "bg-amber-400/15 text-amber-200 border-amber-300/20",
  running: "bg-sky-400/15 text-sky-200 border-sky-300/20",
  completed: "bg-emerald-400/15 text-emerald-200 border-emerald-300/20",
  failed: "bg-red-400/15 text-red-200 border-red-300/20",
};

const PanoGenerator: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [authMode, setAuthMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [prompt, setPrompt] = useState(promptExamples[0]);
  const [negativePrompt, setNegativePrompt] = useState(
    "low quality, blurry, distorted, text, watermark, artifacts, flicker",
  );
  const [seed, setSeed] = useState("");
  const [steps, setSteps] = useState(50);
  const [generations, setGenerations] = useState<Generation[]>([]);
  const [queue, setQueue] = useState<QueueStatus | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const selected = useMemo(
    () => generations.find((item) => item.id === selectedId) || generations[0],
    [generations, selectedId],
  );

  useEffect(() => {
    panoApi
      .me()
      .then(({ user }) => setUser(user))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    const refresh = async () => {
      try {
        const [history, queueStatus] = await Promise.all([
          panoApi.generations(),
          panoApi.queue().catch(() => null),
        ]);
        if (!cancelled) {
          setGenerations(history.generations);
          setQueue(queueStatus);
          if (!selectedId && history.generations[0]) {
            setSelectedId(history.generations[0].id);
          }
        }
      } catch (error) {
        if (!cancelled) setMessage(error instanceof Error ? error.message : "Unable to refresh");
      }
    };

    refresh();
    const timer = window.setInterval(refresh, 8000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [user, selectedId]);

  useEffect(() => {
    if (!selected || !["queued", "running"].includes(selected.status)) return;

    const timer = window.setInterval(async () => {
      const { generation } = await panoApi.generation(selected.id);
      setGenerations((items) =>
        items.map((item) => (item.id === generation.id ? generation : item)),
      );
    }, 6000);
    return () => window.clearInterval(timer);
  }, [selected]);

  const submitAuth = async (event: FormEvent) => {
    event.preventDefault();
    setAuthError("");
    setBusy(true);
    try {
      const action = authMode === "login" ? panoApi.login : panoApi.register;
      const response = await action(email, password);
      setUser(response.user);
      setPassword("");
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  const submitGeneration = async (event: FormEvent) => {
    event.preventDefault();
    setMessage("");
    setBusy(true);
    try {
      const { generation } = await panoApi.generate({
        prompt,
        negative_prompt: negativePrompt,
        seed: seed ? Number(seed) : undefined,
        num_inference_steps: steps,
        width: 896,
        height: 448,
      });
      setGenerations((items) => [generation, ...items]);
      setSelectedId(generation.id);
      setMessage("Prompt polished and job submitted.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await panoApi.logout().catch(() => undefined);
    setUser(null);
    setGenerations([]);
    setSelectedId(null);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-gray-100">
      <Hero user={user} onLogout={logout} />

      <main className="px-4 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
          <motion.section
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-[8px] border border-white/10 bg-white/[0.04] p-4 shadow-2xl shadow-black/30 sm:p-6"
          >
            {user ? (
              <GeneratorForm
                prompt={prompt}
                setPrompt={setPrompt}
                negativePrompt={negativePrompt}
                setNegativePrompt={setNegativePrompt}
                seed={seed}
                setSeed={setSeed}
                steps={steps}
                setSteps={setSteps}
                busy={busy}
                onSubmit={submitGeneration}
              />
            ) : (
              <AuthForm
                mode={authMode}
                setMode={setAuthMode}
                email={email}
                setEmail={setEmail}
                password={password}
                setPassword={setPassword}
                error={authError}
                busy={busy}
                onSubmit={submitAuth}
              />
            )}
          </motion.section>

          <aside className="space-y-6">
            <QueuePanel queue={queue} />
            <HistoryPanel
              generations={generations}
              selectedId={selected?.id || null}
              setSelectedId={setSelectedId}
            />
          </aside>
        </div>

        {message && (
          <div className="mx-auto mt-6 max-w-7xl rounded-[8px] border border-white/10 bg-black/40 px-4 py-3 text-sm text-gray-300">
            {message}
          </div>
        )}

        {selected && <ResultPanel generation={selected} />}
      </main>
    </div>
  );
};

function Hero({ user, onLogout }: { user: User | null; onLogout: () => void }) {
  return (
    <section className="relative mb-8 min-h-[520px] overflow-hidden sm:min-h-[620px]">
      <video
        className="absolute inset-0 h-full w-full object-cover"
        autoPlay
        muted
        loop
        playsInline
      >
        <source src={getCdnUrl("heros/futuristic city.mp4")} type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" />
      <div className="relative z-10 mx-auto flex min-h-[520px] max-w-7xl flex-col px-4 py-6 sm:min-h-[620px] sm:px-6 lg:px-8">
        <nav className="flex items-center justify-between">
          <div className="text-lg font-semibold tracking-[0.18em] text-white">PANOWAN</div>
          <div className="flex items-center gap-3 text-sm text-gray-300">
            {user && <span className="hidden sm:inline">{user.email}</span>}
            {user && (
              <button
                onClick={onLogout}
                className="rounded-[8px] border border-white/15 px-4 py-2 text-gray-200 transition hover:border-white/30 hover:bg-white/10"
              >
                Sign out
              </button>
            )}
          </div>
        </nav>
        <div className="flex flex-1 items-center">
          <div className="max-w-3xl">
            <motion.h1
              initial={{ y: 28, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="text-4xl font-bold leading-tight text-white sm:text-6xl lg:text-7xl"
            >
              PanoWan video generation
            </motion.h1>
            <motion.p
              initial={{ y: 28, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="mt-5 max-w-2xl text-base leading-7 text-gray-200 sm:text-xl"
            >
              Submit concise ideas, let DeepSeek expand them into cinematic panoramic prompts,
              then send queued jobs to the PanoWan video API with persistent history.
            </motion.p>
          </div>
        </div>
      </div>
    </section>
  );
}

function AuthForm(props: {
  mode: "login" | "register";
  setMode: (mode: "login" | "register") => void;
  email: string;
  setEmail: (value: string) => void;
  password: string;
  setPassword: (value: string) => void;
  error: string;
  busy: boolean;
  onSubmit: (event: FormEvent) => void;
}) {
  return (
    <form onSubmit={props.onSubmit} className="mx-auto max-w-md space-y-5">
      <div>
        <h2 className="text-2xl font-semibold text-white">
          {props.mode === "login" ? "Sign in" : "Create account"}
        </h2>
        <p className="mt-2 text-sm text-gray-400">
          Accounts keep generation jobs and completed videos tied to your history.
        </p>
      </div>
      <div className="grid grid-cols-2 rounded-[8px] border border-white/10 bg-black/30 p-1">
        {(["login", "register"] as const).map((mode) => (
          <button
            key={mode}
            type="button"
            onClick={() => props.setMode(mode)}
            className={`rounded-[6px] px-3 py-2 text-sm transition ${
              props.mode === mode ? "bg-white text-gray-950" : "text-gray-300 hover:bg-white/10"
            }`}
          >
            {mode === "login" ? "Login" : "Register"}
          </button>
        ))}
      </div>
      <label className="block">
        <span className="text-sm text-gray-300">Email</span>
        <input
          type="email"
          value={props.email}
          onChange={(event) => props.setEmail(event.target.value)}
          className="mt-2 w-full rounded-[8px] border border-white/10 bg-black/40 px-4 py-3 text-white outline-none transition focus:border-white/40"
          required
        />
      </label>
      <label className="block">
        <span className="text-sm text-gray-300">Password</span>
        <input
          type="password"
          value={props.password}
          onChange={(event) => props.setPassword(event.target.value)}
          className="mt-2 w-full rounded-[8px] border border-white/10 bg-black/40 px-4 py-3 text-white outline-none transition focus:border-white/40"
          minLength={8}
          required
        />
      </label>
      {props.error && <p className="text-sm text-red-300">{props.error}</p>}
      <button
        disabled={props.busy}
        className="w-full rounded-[8px] bg-white px-5 py-3 font-semibold text-gray-950 transition hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {props.busy ? "Working..." : props.mode === "login" ? "Login" : "Register"}
      </button>
    </form>
  );
}

function GeneratorForm(props: {
  prompt: string;
  setPrompt: (value: string) => void;
  negativePrompt: string;
  setNegativePrompt: (value: string) => void;
  seed: string;
  setSeed: (value: string) => void;
  steps: number;
  setSteps: (value: number) => void;
  busy: boolean;
  onSubmit: (event: FormEvent) => void;
}) {
  return (
    <form onSubmit={props.onSubmit} className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-white">Generate panoramic video</h2>
          <p className="mt-2 text-sm text-gray-400">
            Your prompt is polished server-side before it is submitted to the queue.
          </p>
        </div>
        <button
          disabled={props.busy}
          className="rounded-[8px] bg-white px-5 py-3 font-semibold text-gray-950 transition hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {props.busy ? "Submitting..." : "Generate"}
        </button>
      </div>

      <label className="block">
        <span className="text-sm text-gray-300">Prompt</span>
        <textarea
          value={props.prompt}
          onChange={(event) => props.setPrompt(event.target.value)}
          rows={7}
          className="mt-2 w-full resize-y rounded-[8px] border border-white/10 bg-black/40 px-4 py-3 leading-7 text-white outline-none transition focus:border-white/40"
          required
        />
      </label>

      <div className="flex flex-wrap gap-2">
        {promptExamples.map((example) => (
          <button
            key={example}
            type="button"
            onClick={() => props.setPrompt(example)}
            className="rounded-[8px] border border-white/10 px-3 py-2 text-left text-xs text-gray-300 transition hover:border-white/30 hover:bg-white/10"
          >
            {example}
          </button>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-[1fr_160px_160px]">
        <label className="block">
          <span className="text-sm text-gray-300">Negative prompt</span>
          <input
            value={props.negativePrompt}
            onChange={(event) => props.setNegativePrompt(event.target.value)}
            className="mt-2 w-full rounded-[8px] border border-white/10 bg-black/40 px-4 py-3 text-white outline-none transition focus:border-white/40"
          />
        </label>
        <label className="block">
          <span className="text-sm text-gray-300">Seed</span>
          <input
            type="number"
            value={props.seed}
            placeholder="Random"
            onChange={(event) => props.setSeed(event.target.value)}
            className="mt-2 w-full rounded-[8px] border border-white/10 bg-black/40 px-4 py-3 text-white outline-none transition focus:border-white/40"
          />
        </label>
        <label className="block">
          <span className="text-sm text-gray-300">Steps</span>
          <input
            type="number"
            min={1}
            max={80}
            value={props.steps}
            onChange={(event) => props.setSteps(Number(event.target.value))}
            className="mt-2 w-full rounded-[8px] border border-white/10 bg-black/40 px-4 py-3 text-white outline-none transition focus:border-white/40"
          />
        </label>
      </div>
    </form>
  );
}

function QueuePanel({ queue }: { queue: QueueStatus | null }) {
  const totals = queue?.totals;
  return (
    <section className="rounded-[8px] border border-white/10 bg-white/[0.04] p-4">
      <h2 className="text-lg font-semibold text-white">Queue</h2>
      <div className="mt-4 grid grid-cols-3 gap-2">
        <Metric label="Queued" value={totals?.queued ?? "-"} />
        <Metric label="Running" value={totals?.running ?? "-"} />
        <Metric label="Load" value={totals?.load ?? "-"} />
      </div>
      <div className="mt-4 space-y-2">
        {(queue?.workers || []).map((worker) => (
          <div
            key={worker.worker_id}
            className="flex items-center justify-between rounded-[8px] bg-black/30 px-3 py-2 text-sm"
          >
            <span className="text-gray-300">{worker.worker_id}</span>
            <span className={worker.available ? "text-emerald-300" : "text-amber-300"}>
              {worker.running} running / {worker.queued} queued
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-[8px] bg-black/30 p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="mt-1 text-xl font-semibold text-white">{value}</div>
    </div>
  );
}

function HistoryPanel(props: {
  generations: Generation[];
  selectedId: string | null;
  setSelectedId: (id: string) => void;
}) {
  return (
    <section className="rounded-[8px] border border-white/10 bg-white/[0.04] p-4">
      <h2 className="text-lg font-semibold text-white">History</h2>
      <div className="mt-4 max-h-[460px] space-y-2 overflow-y-auto pr-1">
        {props.generations.length === 0 && (
          <p className="text-sm text-gray-500">Submitted jobs will appear here.</p>
        )}
        {props.generations.map((generation) => (
          <button
            key={generation.id}
            onClick={() => props.setSelectedId(generation.id)}
            className={`w-full rounded-[8px] border p-3 text-left transition ${
              props.selectedId === generation.id
                ? "border-white/30 bg-white/10"
                : "border-white/10 bg-black/20 hover:border-white/25"
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <StatusPill status={generation.status} />
              <span className="text-xs text-gray-500">
                {new Date(generation.created_at).toLocaleString()}
              </span>
            </div>
            <p className="mt-2 line-clamp-2 text-sm leading-6 text-gray-300">
              {generation.original_prompt}
            </p>
          </button>
        ))}
      </div>
    </section>
  );
}

function ResultPanel({ generation }: { generation: Generation }) {
  return (
    <section className="mx-auto mt-6 grid max-w-7xl gap-6 lg:grid-cols-[minmax(0,1fr)_420px]">
      <div className="overflow-hidden rounded-[8px] border border-white/10 bg-black/40">
        <div className="aspect-[2/1] bg-black">
          {generation.download_url ? (
            <video
              key={generation.download_url}
              className="h-full w-full object-cover"
              controls
              playsInline
              src={generation.download_url}
            />
          ) : (
            <div className="flex h-full items-center justify-center px-6 text-center text-gray-500">
              Video preview appears when the job is completed.
            </div>
          )}
        </div>
      </div>
      <div className="rounded-[8px] border border-white/10 bg-white/[0.04] p-4">
        <div className="flex items-center justify-between">
          <StatusPill status={generation.status} />
          <span className="text-xs text-gray-500">{generation.worker_id || "Unassigned"}</span>
        </div>
        <h2 className="mt-5 text-lg font-semibold text-white">Polished prompt</h2>
        <p className="mt-3 text-sm leading-7 text-gray-300">{generation.enhanced_prompt}</p>
        {generation.error && <p className="mt-4 text-sm text-red-300">{generation.error}</p>}
        {generation.download_url && (
          <a
            href={generation.download_url}
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-flex rounded-[8px] bg-white px-4 py-2 text-sm font-semibold text-gray-950 transition hover:bg-gray-200"
          >
            Open video
          </a>
        )}
      </div>
    </section>
  );
}

function StatusPill({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${
        statusTone[status] || "border-white/10 bg-white/10 text-gray-300"
      }`}
    >
      {status}
    </span>
  );
}

export default PanoGenerator;
